import type { AdminCapturePaymentResult, AdminReconcileCaptureResult } from '@carcheck/shared';

import { apiClient } from './client';

/**
 * AUT-223 (2S-17) — the SPA half of the operator's manual capture controls.
 *
 *   - `POST /admin/payments/:id/capture`            — settle a live hold by hand.
 *   - `POST /admin/payments/:id/reconcile-capture`  — ask the provider what
 *     happened to a settlement whose answer was lost.
 *
 * Both are role + permission (`process_payouts`) + audit gated server-side, the
 * same gate as `/admin/payouts`, `/admin/cards` and `/admin/refunds`. AUT-138 /
 * AUT-139 removed per-action TOTP step-up, so neither can return
 * `401 totp_step_up_required` — a single unconditional POST is the whole flow.
 *
 * NO list query here and NO query-key prefix of its own: there is no
 * `/admin/payments` collection, only these two actions on a row the order
 * detail already fetched. The mutations invalidate the `['admin','orders']`
 * prefix (the order detail's Платёж tab re-reads the payment) and the
 * `['admin','payouts']` prefix (a successful capture is what CREATES the
 * inspector's payout — AUT-273 — so the payouts surface goes stale too).
 *
 * Wire types come from `@carcheck/shared` — one declaration, both halves
 * compile against it, so the contract cannot drift unnoticed.
 */

// Re-exported so callers keep importing from the api client they already use;
// the single declaration still lives in `@carcheck/shared`.
export type {
  AdminCapturePaymentResult,
  AdminCapturePaymentStatus,
  AdminReconcileCaptureResult,
  AdminReconcileResolution,
} from '@carcheck/shared';

const PAYMENTS_BASE = '/admin/payments';

/**
 * Low-level manual-capture mutation. Empty body — a hold is settled in full on
 * this path; partial settlement belongs to cancellation policy, not to an
 * operator typing a number.
 *
 * Rejects with `409 payment_not_capturable` (not a live hold),
 * `409 payment_capture_already_claimed` (the automatic path won the race — the
 * money still moved exactly once), `502 payment_capture_failed`, or — AUT-301 —
 * `409 payment_hold_window_closed` with `details {paymentId, holdExpiresAt,
 * providerStatus}`.
 *
 * That last one is reachable EVEN WHEN THIS SCREEN OFFERED THE BUTTON, and by
 * design: the SPA and the server read the same `holdExpiresAt` calendar, but
 * past it the server additionally ASKS the provider whether the hold survived.
 * So an expired window is not a reason to hide the control (our 7-day escrow
 * deadline is probably shorter than the bank's real authorization lifetime) —
 * it is a reason to expect this rejection sometimes. `details.providerStatus`
 * is the provider's answer, and `null` means we never got one.
 */
export const postCapturePayment = async (id: string): Promise<AdminCapturePaymentResult> => {
  const { data } = await apiClient.post<AdminCapturePaymentResult>(
    `${PAYMENTS_BASE}/${id}/capture`,
    {},
    // The 409 `code` IS the operator's next instruction; the caller renders it
    // inline. The blanket conflict toast would drown that out.
    { skipAutoErrorToast: true },
  );
  return data;
};

/**
 * Low-level reconcile mutation. A READ against the provider, never a repeated
 * `complete()` — repeating the mutating call to find out what happened is how
 * the money moves twice.
 *
 * Rejects with `409 payment_not_reconcilable` for anything but `capturing`.
 */
export const postReconcileCapture = async (id: string): Promise<AdminReconcileCaptureResult> => {
  const { data } = await apiClient.post<AdminReconcileCaptureResult>(
    `${PAYMENTS_BASE}/${id}/reconcile-capture`,
    {},
    { skipAutoErrorToast: true },
  );
  return data;
};
